import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  listCostItemTemplates,
  createCostItemTemplate,
  updateCostItemTemplate,
  deleteCostItemTemplate,
} from '../services/commissionService';
import type {
  CostItemTemplate,
  CostItemCategory,
  CreateCostItemTemplateRequest,
} from '../types/commission';
import { NumericFormat } from 'react-number-format';

const CATEGORIES: { value: CostItemCategory; label: string }[] = [
  { value: 'campaign_fee', label: 'Campaign Fee' },
  { value: 'doc_sign_fee', label: 'Document Signing Fee' },
  { value: 'ad_hoc', label: 'Ad-hoc Expense' },
  { value: 'other', label: 'Other' },
];

interface CostItemTemplatesProps {
  agencySlug: string;
  showTitle?: boolean;
}

const CostItemTemplates: React.FC<CostItemTemplatesProps> = ({
  agencySlug,
  showTitle = true,
}) => {
  const [items, setItems] = useState<CostItemTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CostItemTemplate | null>(null);
  const [form, setForm] = useState<CreateCostItemTemplateRequest & { amount: number }>({
    name: '',
    category: 'campaign_fee',
    amount: 0,
    addByDefault: false,
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await listCostItemTemplates(agencySlug);
      setItems(list);
    } catch (e: any) {
      setError(e.message || 'Failed to load cost items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (agencySlug) load();
  }, [agencySlug]);

  const handleOpenAdd = () => {
    setEditing(null);
    setForm({ name: '', category: 'campaign_fee', amount: 0, addByDefault: false });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item: CostItemTemplate) => {
    setEditing(item);
    setForm({
      name: item.name,
      category: item.category,
      amount: item.amount ?? 0,
      addByDefault: item.addByDefault,
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const amount = typeof form.amount === 'number' && form.amount >= 0 ? form.amount : 0;
      if (editing) {
        await updateCostItemTemplate(agencySlug, editing.id, {
          name: form.name.trim(),
          category: form.category,
          amount,
          addByDefault: form.addByDefault,
        });
      } else {
        await createCostItemTemplate(agencySlug, {
          name: form.name.trim(),
          category: form.category,
          amount,
          addByDefault: form.addByDefault,
        });
      }
      await load();
      handleClose();
    } catch (e: any) {
      setError(e.message || 'Failed to save cost item');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAddByDefault = async (item: CostItemTemplate) => {
    try {
      setError(null);
      await updateCostItemTemplate(agencySlug, item.id, {
        addByDefault: !item.addByDefault,
      });
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to update');
    }
  };

  const handleDelete = async (item: CostItemTemplate) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      setError(null);
      await deleteCostItemTemplate(agencySlug, item.id);
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to delete');
    }
  };

  const getCategoryLabel = (c: CostItemCategory) =>
    CATEGORIES.find((x) => x.value === c)?.label ?? c;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        {showTitle && (
          <Typography variant="h6" gutterBottom>
            Cost Items (Property Sales)
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define cost items for property sales. Set a default cost; items with &quot;Add by
          default&quot; are automatically added to each property&apos;s cost of sales when first
          viewed.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            Add cost item
          </Button>
        </Box>
        {items.length === 0 ? (
          <Alert severity="info">
            No cost items yet. Add items such as Campaign Fee, Document Signing Fee, etc. Set a
            default cost and toggle &quot;Add by default&quot; to have them pre-added to every
            property&apos;s cost of sales.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell>Add by default</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={getCategoryLabel(item.category)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      ${(item.amount ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item.addByDefault}
                            onChange={() => handleToggleAddByDefault(item)}
                            color="primary"
                          />
                        }
                        label={item.addByDefault ? 'Yes' : 'No'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(item)}
                        color="primary"
                        aria-label="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item)}
                        color="error"
                        aria-label="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit cost item' : 'Add cost item'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g. Campaign Fee, Photography"
            />
            <TextField
              select
              label="Category"
              value={form.category}
              onChange={(e) =>
                setForm({ ...form, category: e.target.value as CostItemCategory })
              }
              fullWidth
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <NumericFormat
              customInput={TextField}
              label="Cost ($)"
              value={form.amount}
              onValueChange={(values) => {
                setForm({ ...form, amount: values.floatValue ?? 0 });
              }}
              thousandSeparator=","
              decimalScale={2}
              allowNegative={false}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.addByDefault}
                  onChange={(e) => setForm({ ...form, addByDefault: e.target.checked })}
                  color="primary"
                />
              }
              label="Add to property cost of sales by default"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CostItemTemplates;
